import { useEffect, useId, useRef, useState } from 'react';
import type { FormEvent } from 'react';
import { useAppState, useStore } from '../app/context.js';
import { copy } from '../copy.js';
import { cls } from '../parts/classes.js';
import { Picture } from '../parts/Picture.js';
import { pfpOf } from '../select/picture.js';
import { chooseColour } from '../state/session.js';
import { uiError } from '../state/store.js';
import { color, layer, palette, shadow } from '../tokens.js';
import { Wordmark } from './Wordmark.js';

/**
 * "Choose your colour", once, right after an account's first sign-in.
 *
 * NOT FROM THE PROTOTYPE. The owner decided on 3 October 2026 that the app asks for the colour then
 * (every member otherwise starts with the same blue); the backend cannot take a colour at sign-up.
 * It uses the sign-in screen's layout (title at 440, 150; the 560 px column; the island with a quiet
 * and a filled button), your picture as the profile header draws it (72 px), and the Look tray's
 * colour swatches. One palette colour is chosen at random to start with, so members who just press
 * Continue still differ. Implementation defaults, listed in the log.
 */
export function ColourStep({ onSignOut }: { readonly onSignOut: () => void }) {
  const s = useStore(), st = useAppState();
  const me = st.session?.me;
  const [colour, setColour] = useState<string>(() => palette[Math.floor(Math.random() * palette.length)] ?? palette[0]);
  const [busy, setBusy] = useState(false), [error, setError] = useState('');
  const formId = useId();
  const live = useRef(true);
  useEffect(() => { live.current = true; return () => { live.current = false; }; }, []);

  async function submit(event: FormEvent) {
    event.preventDefault();
    if (busy) return;
    setBusy(true); setError('');
    try { await chooseColour(s, colour); }
    catch (reason) {
      const kind = uiError(reason).kind;
      if (kind === 'auth') { s.report({ kind }); return; }
      if (live.current) setError(kind === 'network' ? copy.auth.error.network : copy.auth.error.server);
    } finally { if (live.current) setBusy(false); }
  }

  const picture = pfpOf(st, colour, me?.pic);
  return <>
    <Wordmark />
    <div style={{ position: 'absolute', left: 440, top: 150, width: 560, height: 44, zIndex: layer.header, display: 'flex', alignItems: 'center' }}>
      <h1 tabIndex={-1} data-v1-title="" style={{ margin: 0, fontSize: 26, fontWeight: 500, letterSpacing: '-0.01em', color: color.textStrong, whiteSpace: 'nowrap', outline: 'none' }}>{copy.auth.colourTitle}</h1>
    </div>
    <form id={formId} data-v1-colour-step="" noValidate onSubmit={(event) => { void submit(event); }} style={{ position: 'absolute', left: 440, top: 236, width: 560, zIndex: layer.surface }}>
      <p style={{ margin: '0 0 22px', fontSize: 17, lineHeight: 1.45, color: color.text }}>{copy.auth.colourBody}</p>
      <div style={{ display: 'flex', alignItems: 'center', gap: 18, marginBottom: 14 }}>
        <span aria-hidden="true" data-v1-colour-preview="" style={{ flex: 'none', position: 'relative', width: 72, height: 72, borderRadius: '50%', overflow: 'hidden', isolation: 'isolate', background: colour }}>
          <Picture p={picture} grain />
        </span>
        <div role="group" aria-label={copy.auth.colourTitle} style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
          {palette.map((c, i) => {
            const on = c === colour;
            return <button key={c} type="button" className={cls('ctl', 'qb')} aria-label={copy.hub.colour(i + 1, on)} aria-pressed={on} disabled={busy} autoFocus={on} onClick={() => setColour(c)}
              style={{ flex: 'none', width: 44, height: 44, padding: 0, border: 'none', borderRadius: '50%', background: 'transparent', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
              <span style={{ width: 24, height: 24, borderRadius: '50%', background: c, boxShadow: `0 0 0 ${on ? '2px' : '0px'} ${color.paper}` }} />
            </button>;
          })}
        </div>
      </div>
      {error && <div role="alert" data-v1-auth-error="" style={{ marginBottom: 10, fontSize: 14, lineHeight: 1.45, color: color.danger }}>{error}</div>}
    </form>
    <div data-v1-island="colour" style={{ position: 'absolute', left: 520, top: 804, width: 400, height: 64, zIndex: layer.island, boxSizing: 'border-box', padding: '0 10px', display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 8, borderRadius: 32, background: color.panel88, boxShadow: shadow.island, backdropFilter: 'blur(16px)', WebkitBackdropFilter: 'blur(16px)' }}>
      <button type="button" className={cls('ctl', 'qb')} data-v1-auth-switch="" disabled={busy} onClick={onSignOut} style={{ flex: 'none', height: 44, padding: '0 14px', border: 'none', borderRadius: 22, background: 'transparent', color: color.textMuted, fontFamily: 'inherit', fontSize: 14, cursor: 'pointer' }}>{copy.auth.signOut}</button>
      <button type="submit" form={formId} className={cls('ctl', 'pb')} data-v1-auth-submit="" disabled={busy}
        style={{ flex: 'none', height: 44, padding: '0 20px', border: 'none', borderRadius: 22, background: busy ? color.tileHover : color.paper, color: busy ? color.textFaint : color.ink, fontFamily: 'inherit', fontSize: 14, fontWeight: 500, cursor: busy ? 'default' : 'pointer' }}>{busy ? copy.auth.finishing : copy.auth.finish}</button>
    </div>
  </>;
}
